'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { ExternalLink, FileText, User } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';

import {
  type IndividualProfile,
  IndividualProfileSchema,
} from '../../schema/professional-profile.schema';
import {
  DOCTOR_REGISTRY_URL,
  NURSE_MIDWIFE_REGISTRY_URL,
  PHARMACIST_REGISTRY_URL,
} from './constants';
import {
  IconInput,
  RequiredFormLabel,
  WizardFormFooter,
} from './form-components';
import { useProfileFormSubmit } from './use-profile-form-submit';

interface IndividualDetailsStepProps {
  userType: 'doctor' | 'pharmacist' | 'nurse_midwife';
  userId: string;
  onBack: () => void;
  onComplete: () => void;
}

export function IndividualDetailsStep({
  userType,
  userId,
  onBack,
  onComplete,
}: IndividualDetailsStepProps) {
  const { t } = useTranslation('account');
  const { submitProfile } = useProfileFormSubmit(onComplete);

  const registryUrl =
    userType === 'doctor'
      ? DOCTOR_REGISTRY_URL
      : userType === 'pharmacist'
        ? PHARMACIST_REGISTRY_URL
        : NURSE_MIDWIFE_REGISTRY_URL;

  const registryName =
    userType === 'doctor'
      ? 'Centralnym Rejestrze Lekarzy'
      : userType === 'pharmacist'
        ? 'Centralnym Rejestrze Farmaceutów'
        : 'Centralnym Rejestrze Pielęgniarek i Położnych';

  const placeholder =
    userType === 'doctor'
      ? '1234567'
      : userType === 'pharmacist'
        ? '12345678'
        : '1234567P';

  const maxLength =
    userType === 'doctor' ? 7 : userType === 'pharmacist' ? 8 : 8;

  const form = useForm<IndividualProfile>({
    resolver: zodResolver(IndividualProfileSchema),
    defaultValues: {
      userType,
      fullName: '',
      licenseNumber: '',
    },
  });

  const onSubmit = (data: IndividualProfile) =>
    submitProfile({
      userId,
      userType: data.userType,
      publicData: {
        name: data.fullName,
        license_number:
          data.userType === 'nurse_midwife'
            ? data.licenseNumber.toUpperCase()
            : data.licenseNumber,
      },
    });

  return (
    <div className="space-y-6 pt-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <RequiredFormLabel>
                  {t('profileWizard.fullName')}
                </RequiredFormLabel>
                <FormControl>
                  <IconInput
                    icon={User}
                    {...field}
                    placeholder="Jan Kowalski"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="licenseNumber"
            render={({ field }) => (
              <FormItem>
                <RequiredFormLabel>Numer PWZ</RequiredFormLabel>
                <FormControl>
                  <IconInput
                    icon={FileText}
                    {...field}
                    placeholder={placeholder}
                    maxLength={maxLength}
                  />
                </FormControl>
                <FormDescription className="text-muted-foreground text-[11px]">
                  Sprawdź w{' '}
                  <a
                    href={registryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-foreground inline-flex items-center gap-0.5 underline underline-offset-2"
                  >
                    {registryName}
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <WizardFormFooter
            onBack={onBack}
            isSubmitting={form.formState.isSubmitting}
          />
        </form>
      </Form>
    </div>
  );
}
